import { Box, Card, IconButton, InputAdornment, MenuItem, Select, SelectChangeEvent, Table, TableBody, TablePagination, TextField, Typography, CircularProgress, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import * as PXBColors from '@brightlayer-ui/colors';
import { useLanguageLocale } from "../../../../hooks/language-locale-hooks";
import { selectStyle } from "../../selectStyle";
import { Close, Search } from "@mui/icons-material";
import { EnhancedTableHead } from "../../../../components/Table/EnhancedTableHead";
import { HeadCell, Order } from "../../../../types/table";
import ConfigureLumnListItems, { zoneArray } from "./ConfigureLumnListItems";
import { defaultconfigureLumnListData, WhitelistZoneData } from "../../../../state/assets-state";
import { useDispatch, useSelector } from "react-redux";
import { Selectors } from "../../../../selectors";
import { EmptyState } from "@brightlayer-ui/react-components";
import { ConfigureLuminaryDetailsList } from "../../../../types/configure-luminaries-list-types";
import { useDebounce } from "use-debounce";
import { AssetThunks } from "../../../../actions/thunks/assets-thunks";

type Props = {
    deviceWhitelistZone: WhitelistZoneData | null;
    selectedWhitelistId: string;
    selectedZone: string;
    setSelectedZone: (zone: string) => void;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    rowsPerPage: number;
    setRowsPerPage: (rows: number) => void;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    offset: number;
    setOffset: (offset: number) => void;
    onHasChanges?: (hasChanges: boolean) => void;
};



const ConfigureLuminaires: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (props: Props) => {
    const { t } = useLanguageLocale();
    const dispatch = useDispatch();
    const asset = useSelector(Selectors.Asset.assets);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState('ZonesName');
    const [totalRecords, setTotalRecords] = React.useState(10);
    const [configureLumnListData, setConfigureLumnListData] = useState<ConfigureLuminaryDetailsList>(defaultconfigureLumnListData);
    const [configureLumnListLoaded, setConfigureLumnListLoaded] = React.useState(false);
    const [isDataLoaded, setIsDataLoaded] = React.useState(true);
    const [emptyConfigureLumnList, setEmptyConfigureLumnList] = React.useState(false);
    const [localSearch, setLocalSearch] = useState(props.searchQuery);
    const [debouncedLocalSearch] = useDebounce(localSearch, 3000);
    const [changes, setChanges] = useState(false);
    const [nameErrors, setNameErrors] = useState<{ [id: string]: boolean }>({});
    const [testGroupErrors, setTestGroupErrors] = useState<{ [id: string]: boolean }>({});
    const [zoneErrors, setZoneErrors] = useState<{ [id: string]: boolean }>({});

    const headCells: HeadCell[] = [
        { id: 'zone', label: t('DEVICE_DETAILS.ZONES'), hideSorting: true },
        { id: 'name', label: t('DEVICE_DETAILS.NAME'), hideSorting: true },
        { id: 'epas', label: t('ASSETS_DETAILS.EPAS'), hideSorting: true },
        { id: 'productFamily', label: t('ASSETS_DETAILS.PRODUCT_FAMILY'), hideSorting: true },
        { id: 'category', label: t('ASSETS_DETAILS.CATEGORY'), hideSorting: true },
        { id: 'partNo', label: t('DEVICE_DETAILS.PART_NUMBER'), hideSorting: true },
        { id: 'testGroup', label: t('DEVICE_DETAILS.TEST_GROUP'), hideSorting: true },
        { id: 'ratedDuration', label: t('ASSETS_DETAILS.RATED_DURATION'), hideSorting: true },
        { id: 'optionalDescription', label: t('ASSETS_DETAILS.OPTIONAL_DESCRIPTION'), hideSorting: true },
    ];

    const handleZoneList = (event: SelectChangeEvent) => {
        props.setSelectedZone(event.target.value);
    };

    const resetSearch = (): void => {
        props.setSearchQuery('');
    };

    useEffect(() => {
        props.setSearchQuery(debouncedLocalSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedLocalSearch]);

    useEffect(() => {
        if (asset.configureLuminariesListLoading) {
            setIsDataLoaded(true);
            return;
        }
        if (asset.configureLuminariesListSuccess) {
            setEmptyConfigureLumnList(asset.configureLuminariesListDetails.luminaryConfigs.length === 0);
            setConfigureLumnListData(asset.configureLuminariesListDetails);
            setTotalRecords(asset.configureLuminariesListDetails.totalRecordCount);
            setConfigureLumnListLoaded(true);
            setIsDataLoaded(false);
        } else if (asset.configureLuminariesListFail) {
            setIsDataLoaded(false);
            setEmptyConfigureLumnList(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asset.configureLuminariesListLoading, asset.configureLuminariesListSuccess, asset.configureLuminariesListFail, asset.configureLuminariesListDetails]);

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        props.setCurrentPage(newPage);
        props.setOffset(newPage + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        props.setRowsPerPage(parseInt(event.target.value, 10));
        props.setCurrentPage(0);
        props.setOffset(1);
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: any): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleNameChange = (rowId: number, newName: string) => {
        setConfigureLumnListData(prevData => ({
            ...prevData,
            luminaryConfigs: prevData.luminaryConfigs.map(row =>
                String(row.id) === String(rowId) ? { ...row, name: newName } : row
            )
        }));
    };
    const handleZoneChange = (rowId: number, newZoneId: string | number) => {
        setConfigureLumnListData(prevData => ({
            ...prevData,
            luminaryConfigs: prevData.luminaryConfigs.map(row =>
                String(row.id) === String(rowId)
                    ? { ...row, zone: typeof newZoneId === 'string' ? Number(newZoneId) : newZoneId }
                    : row
            )
        }));
    };

    const handleTestGroupChange = (rowId: number, newTestGroupId: number) => {
        setConfigureLumnListData(prevData => ({
            ...prevData,
            luminaryConfigs: prevData.luminaryConfigs.map(row =>
                String(row.id) === String(rowId) ? { ...row, testGroupId: newTestGroupId } : row
            )
        }));
    };

    const handleDescriptionChange = (rowId: number, newDescription: string) => {
        setConfigureLumnListData(prevData => ({
            ...prevData,
            luminaryConfigs: prevData.luminaryConfigs.map(row =>
                String(row.id) === String(rowId) ? { ...row, description: newDescription } : row
            )
        }));
    };
    useEffect(() => {
        if (!configureLumnListData?.luminaryConfigs?.length ||
            !asset.configureLuminariesListDetails?.luminaryConfigs?.length) {
            setChanges(false);
            setNameErrors({});
            setTestGroupErrors({});
            setZoneErrors({}); //  Clear zone errors
            if (props.onHasChanges) {
                props.onHasChanges(false);
            }
            return;
        }

        let hasChanges = false;
        let hasEmptyName = false;
        let hasInvalidTestGroup = false;
        let hasInvalidZone = false; //  Track invalid zones
        const nameErrorsMap: { [id: string]: boolean } = {};
        const testGroupErrorsMap: { [id: string]: boolean } = {};
        const zoneErrorsMap: { [id: string]: boolean } = {}; //  Track zone errors
        const currentList = configureLumnListData.luminaryConfigs;
        const originalList = asset.configureLuminariesListDetails.luminaryConfigs;

        // Valid test group IDs (from dropdown)
        const validTestGroupIds = [1, 2, 3, 4];
        //  Valid zone IDs (1-16 from zoneArray)
        const validZoneIds = Array.from({ length: 16 }, (_, i) => i + 1);

        for (let i = 0; i < currentList.length; i++) {
            const current = currentList[i];
            const original = originalList.find(o => String(o.id) === String(current.id));

            // Check if name is empty (mandatory field)
            if (!current.name || current.name.trim() === '') {
                hasEmptyName = true;
                nameErrorsMap[current.id] = true;
            } else {
                nameErrorsMap[current.id] = false;
            }

            // Check if test group is invalid (not in valid list or is 0/null)
            if (!current.testGroupId || !validTestGroupIds.includes(current.testGroupId)) {
                hasInvalidTestGroup = true;
                testGroupErrorsMap[current.id] = true;
            } else {
                testGroupErrorsMap[current.id] = false;
            }

            //  Check if zone is invalid (not in valid list or is 0/null)
            if (!current.zone || !validZoneIds.includes(current.zone)) {
                hasInvalidZone = true;
                zoneErrorsMap[current.id] = true;
            } else {
                zoneErrorsMap[current.id] = false;
            }

            if (original) {
                if (
                    current.name !== original.name ||
                    current.zone !== original.zone ||
                    current.testGroupId !== original.testGroupId ||
                    current.description !== original.description
                ) {
                    hasChanges = true;
                }
            }
        }

        setNameErrors(nameErrorsMap);
        setTestGroupErrors(testGroupErrorsMap);
        setZoneErrors(zoneErrorsMap); //  Update zone errors
        //  Only enable save if all validations pass
        const canSave = hasChanges && !hasEmptyName && !hasInvalidTestGroup && !hasInvalidZone;
        setChanges(canSave);

        if (props.onHasChanges) {
            props.onHasChanges(canSave);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configureLumnListData, asset.configureLuminariesListDetails, props.onHasChanges]);

    const handleSave = () => {
        if (!props.selectedWhitelistId || !configureLumnListData?.luminaryConfigs?.length) {
            return;
        }

        // Valid test group IDs
        const validTestGroupIds = [1, 2, 3, 4];
        //  Valid zone IDs
        const validZoneIds = Array.from({ length: 16 }, (_, i) => i + 1);

        // Double-check validation before saving
        const hasEmptyName = configureLumnListData.luminaryConfigs.some(
            config => !config.name || config.name.trim() === ''
        );

        // Check for invalid test groups
        const hasInvalidTestGroup = configureLumnListData.luminaryConfigs.some(
            config => !config.testGroupId || !validTestGroupIds.includes(config.testGroupId)
        );

        //  Check for invalid zones
        const hasInvalidZone = configureLumnListData.luminaryConfigs.some(
            config => !config.zone || !validZoneIds.includes(config.zone)
        );

        if (hasEmptyName) {
            return;
        }

        if (hasInvalidTestGroup) {
            return;
        }

        if (hasInvalidZone) {
            return;
        }

        // Build luminaryConfigs array
        const luminaryConfigsArray = configureLumnListData.luminaryConfigs.map(config => ({
            id: config.id,
            name: config.name || '',
            description: config.description || '',
            lumModelName: config.lumModelName || '',
            lumModelDescription: config.lumModelDescription || '',
            lumPartNumber: config.lumPartNumber || '',
            numberOfNeighbors: config.numberOfNeighbors || 0,
            radioSignal: config.radioSignal || 0,
            radioPartNumber: config.radioPartNumber || '',
            radioModelName: config.radioModelName || '',
            radioModelDescription: config.radioModelDescription || '',
            epasNumber: config.epasNumber || '',
            productFamily: config.productFamily || '',
            ratedDuration: config.ratedDuration || 0,
            category: config.category || '',
            address: config.address || '0',
            isActive: config.isActive ?? true,
            zone: config.zone || 0,
            zonePosition: 0,
            testGroupId: config.testGroupId || 0,
            epasProductImgUrl: ''
        }));

        // Pass whitelistId as first param, array as second
        dispatch(AssetThunks.setConfigureLuminairesList(
            props.selectedWhitelistId,
            luminaryConfigsArray
        ));
    };


    const handleCancel = () => {
        //  Reset to original API data
        if (asset.configureLuminariesListDetails?.luminaryConfigs?.length) {
            setConfigureLumnListData(asset.configureLuminariesListDetails);
            setChanges(false);

            //  Notify parent that no changes
            if (props.onHasChanges) {
                props.onHasChanges(false);
            }
        }
    };

    return (
        <>
            <div data-testid="configure-luminaires-container">
                <Box style={{
                    backgroundSize: 'contain',
                    alignItems: 'center',
                }}>
                    {changes && (
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 100,
                                backgroundColor: PXBColors.blue[50],
                                padding: '12px 16px',
                                marginBottom: 2,
                                borderRadius: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}
                        >
                            <Typography variant="body2" color={PXBColors.blue[700]}>
                                {t('ASSETS_DETAILS.ZONES_CONFIGURATION_UNSAVED_CHANGES') || 'You have unsaved changes'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleCancel}
                                    data-testid="cancel-button"
                                >
                                    {t('COMMON.CANCEL') || 'Cancel'}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    data-testid="save-button"
                                >
                                    {t('COMMON.SAVE') || 'Save'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
                        <Select
                            id="zones-list"
                            data-testid="zones-list"
                            style={{
                                width: '15%',
                                height: 58,
                                borderBlock: 0,
                                outline: 'none',
                                overflow: 'hidden',
                                fontSize: 14,
                                color: PXBColors.black[500],
                                fontStyle: 'normal',
                                borderRadius: 5,
                                fontWeight: 400,
                                backgroundColor: PXBColors.white[50],
                            }}
                            displayEmpty
                            value={props.selectedZone}
                            onChange={handleZoneList}
                        >
                            <MenuItem value=""
                                style={selectStyle.dropdown}> {t('DEVICE_DETAILS.ALL_ZONES')}</MenuItem>
                            {zoneArray.map((zone) => {
                                return (
                                    <MenuItem key={zone.id} value={zone.id}>
                                        {zone.id}. {zone.zone}
                                    </MenuItem>
                                )
                            })}
                        </Select>
                        <TextField
                            inputProps={{ 'data-testid': 'searchText-field', maxLength: 100 }}
                            value={localSearch}
                            style={{
                                width: '40%',
                                borderRadius: 5,
                                backgroundColor: PXBColors.white[50],
                                fontSize: 12,
                                fontWeight: 400,
                            }}
                            placeholder={t('ORGANIZATION_MANAGE.SEARCH_BUILDINGS')}
                            onChange={e => setLocalSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search data-testid="search-icon" style={{ color: PXBColors.gray[500] }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {props.searchQuery !== '' && (
                                            <IconButton onClick={resetSearch}>
                                                <Close data-testid="close-search-icon" fontSize="small" />
                                            </IconButton>
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                            variant="outlined"
                        />
                    </Box>
                    <Card style={{ flex: '1 1 0px', overflow: 'auto', marginTop: '2%' }} id="configuration-lumns-table" data-testid="configuration-lumns-table">
                        <Box sx={{ width: '100%', overflowX: 'auto' }}>
                            {/* Only show table when NOT loading */}
                            {!asset.configureLuminariesListLoading && !asset.setConfigureLuminariesListLoading && (
                                <Table
                                    stickyHeader
                                    size="small"
                                    id="configLumn-list-table"
                                    data-testid="configLumn-list-table"
                                    style={{ minWidth: 1800 }}>
                                    <EnhancedTableHead
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={handleRequestSort}
                                        headCells={headCells}
                                    />
                                    {!isDataLoaded && !emptyConfigureLumnList && configureLumnListLoaded &&
                                        <TableBody className="tableHover" style={{ minHeight: 100 }} key={'mainTable'}
                                            id="config-lumns-list-table-body"
                                            data-testid="config-lumns-list-table-body">
                                            {configureLumnListData.luminaryConfigs.map((row) => (
                                                <ConfigureLumnListItems
                                                    key={Number(row.id)}
                                                    row={row}
                                                    onNameChange={handleNameChange}
                                                    onZoneChange={handleZoneChange}
                                                    onDescriptionChange={handleDescriptionChange}
                                                    onTestGroupChange={handleTestGroupChange}
                                                    hasNameError={nameErrors[row.id] || false}
                                                    hasTestGroupError={testGroupErrors[row.id] || false}
                                                    hasZoneError={zoneErrors[row.id] || false} //  Pass zone error
                                                />
                                            ))}
                                        </TableBody>}
                                </Table>
                            )}
                        </Box>
                        {/* Show loader when EITHER loading state is true */}
                        {(asset.configureLuminariesListLoading || asset.setConfigureLuminariesListLoading) && (
                            <EmptyState
                                style={{ flex: 1, minHeight: 400, backgroundColor: PXBColors.white[50] }}
                                icon={<CircularProgress id="progress-spinner" />}
                                title=""
                                placeholder={undefined}
                            />
                        )}
                        {/*  Show empty state only when NOT loading and no data */}
                        {!asset.configureLuminariesListLoading && !asset.setConfigureLuminariesListLoading &&
                            ((!isDataLoaded && emptyConfigureLumnList) || (Array.isArray(asset.getWhitelistDetail) && asset.getWhitelistDetail.length === 0)) && (
                                <EmptyState
                                    style={{ flex: 1, minHeight: 400, backgroundColor: PXBColors.white[50] }}
                                    title={t('ORGANIZATION_MANAGE.ERROR_NO_LUMINARY_FOUND')}
                                    icon={undefined}
                                    placeholder={undefined}
                                />
                            )}
                    </Card >
                    {!emptyConfigureLumnList && <TablePagination
                        component="div"
                        data-testid="pagination-users-service-partner"
                        count={totalRecords}
                        page={props.currentPage}
                        onPageChange={handleChangePage}
                        rowsPerPage={props.rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={t('LABELS.ITEMS_PER_PAGE')}
                        showFirstButton showLastButton
                    />}
                </Box>
            </div>
        </>
    )
}
export default ConfigureLuminaires;
